import { useRecoilCallback } from 'recoil';
import {
  latestByEndMarkAtom,
  latestMeasuredByEndMarkAtom,
  MeasureDetail,
  MeasureEntry,
  ttlThresholds,
  prevEndMark,
  MeasureMetadata,
  conflictingEndMark,
} from './state';
import { nanoid } from 'nanoid';
import { createMarkForId } from 'utils/performance';
import { measureNowIfStarted } from 'utils/measureNowIfStarted';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { afterPaint } from './utils';
import { measures } from './measures';
import analytics from 'modules/analytics';
import { AnalyticEventName } from 'modules/analytics/analytics';

/**
 *
 * @param measure - the measure entry to start, which includes metricName, startMarkName, endMarkName
 * @param opts - optional object that can include:
 *  - id: a unique identifier for this measurement run; if not provided, a new one will be generated
 *  - details: optional details to associate with this measurement run
 *
 * Start (or replace) a measurement for a given measure entry.
 * - Cancels & clears a previous claimant for the SAME end mark (even if it was a different metric).
 * - Marks the new start.
 * - Stores optional details with the run (to be included when we emit the measurement).
 * Returns the runId used.
 */
export const useStartMeasurement = () =>
  useRecoilCallback(
    ({ set, snapshot }) =>
      (
        measure: MeasureEntry,
        opts?: { id?: string; detail?: MeasureDetail; metadata?: MeasureMetadata }
      ): string => {
        const id = opts?.id ?? nanoid();
        const endKey = measure.endMarkName;

        // 1) Cancel & clear previous claimant for this end mark (might be a different metric)
        const prev = snapshot.getLoadable(latestByEndMarkAtom(endKey)).valueMaybe();
        if (prev) {
          performance.clearMarks(createMarkForId(prev.startMarkName, prev.id));
        }

        // 2) Mark new start mark
        performance.mark(createMarkForId(measure.startMarkName, id));

        // 3) Publish as the latest claimant
        set(latestByEndMarkAtom(endKey), {
          id,
          ...measure,
          ...(opts?.detail ? { detail: opts.detail } : {}),
          ...(opts?.metadata ? { metadata: opts.metadata } : {}),
        });

        return id;
      },
    []
  );

/**
 *
 * @param flag - the boolean flag to watch
 * @param ready - whether the flow being measured is ready
 * @param endMarkName - the end mark to measure
 * @param detail - optional details to associate with this measurement
 *
 * When the flag goes from false to true, and the `ready` flag is also true, measure the given end mark immediately.
 */
export function useRisingEdgeMeasure({
  flag,
  ready,
  endMarkName,
  detail,
  measureFn,
}: {
  flag: boolean;
  ready: boolean;
  endMarkName: string;
  detail?: MeasureDetail;
  measureFn: (endMarkName: string, ready: boolean, detail?: MeasureDetail) => void;
}) {
  const prevCanMeasureRef = useRef<boolean>(flag && ready);

  useEffect(() => {
    const canMeasure = flag && ready;
    const risingEdge = canMeasure && !prevCanMeasureRef.current;

    prevCanMeasureRef.current = canMeasure;

    if (risingEdge) {
      measureFn(endMarkName, true, detail);
    }
  }, [flag, ready, endMarkName, measureFn, detail]);
}

export function useFallingEdgeMeasure({
  flag,
  ready,
  endMarkName,
  shouldFire,
  detail,
  measureFn,
}: {
  flag: boolean;
  ready: boolean;
  endMarkName: string;
  shouldFire?: () => boolean;
  detail?: MeasureDetail;
  measureFn: (endMarkName: string, ready: boolean, detail?: MeasureDetail) => void;
}) {
  const prev = useRef(false);
  const firedForThisCycle = useRef(false);

  useLayoutEffect(() => {
    const falling = !flag && prev.current;

    if (flag && !prev.current) {
      firedForThisCycle.current = false;
    }

    if (falling && ready && !firedForThisCycle.current && (shouldFire?.() ?? true)) {
      firedForThisCycle.current = true;
      measureFn(endMarkName, true, detail);
    }

    prev.current = flag;
  }, [flag, ready, endMarkName, shouldFire, detail, measureFn]);
}

export function useStartAllLoadMeasurements() {
  const startMeasurement = useStartMeasurement();

  return useCallback(
    (measureDetail: MeasureDetail) => {
      const loadID = nanoid();
      const startedAt = performance.now();

      const detail = {
        ...measureDetail,
        loadID,
        startedAt,
      };

      startMeasurement(measures.worklist.load.timeToRequest, {
        detail,
      });

      startMeasurement(measures.worklist.load.timeToDataReady, {
        detail,
      });

      startMeasurement(measures.worklist.load.timeToPaint, {
        detail,
      });

      return loadID;
    },
    [startMeasurement]
  );
}

/**
 *
 * @param endMarkName - the end mark to measure
 * @param ready - whether the flow being measured is ready
 *
 * Measure the metric immediately, if the start mark exists and the `ready` flag is true.
 * - Gets the latest active entry for the given end mark
 * - Checks to see if the start mark exists for that entry
 * - If so, marks the end and measures
 * - Clears the active entry to prevent double-measurement
 * Returns the runId used.
 */
export const useMeasureNowIfReady = () =>
  useRecoilCallback(
    ({ set, snapshot }) =>
      (endMarkName: string, ready: boolean, additionalDetails?: MeasureDetail) => {
        if (!ready) {
          return;
        }

        const curr = snapshot.getLoadable(latestByEndMarkAtom(endMarkName)).valueMaybe();
        if (!curr) {
          return;
        }

        const { metricName, startMarkName, id, detail } = curr;

        const ok = measureNowIfStarted({
          metricName,
          startMarkName,
          endMarkName,
          id,
          detail: { ...(detail ?? {}), ...(additionalDetails ?? {}) },
        });

        if (ok) {
          set(latestByEndMarkAtom(endMarkName), null);
          set(latestMeasuredByEndMarkAtom(endMarkName), {
            ...curr,
            detail: { ...(detail ?? {}), ...(additionalDetails ?? {}) },
          });
        }
      },
    []
  );

/**
 *
 * @param endMarkName - the end mark to measure
 * @param ready - whether the flow being measured is ready
 *
 * Measure the metric immediately, if the start mark exists and the `ready` flag is true and
 * the required previous measurement (if any) was done with the same loadID.
 * - Gets the latest active entry for the given end mark
 * - Checks to see if the measurement is likely stale (based on TTL); if so, abandons it
 * - Checks to see if the required previous measurement was done with the same loadID
 * - Checks to see if the start mark exists for that entry
 * - If so, marks the end and measures
 * - Clears the active entry to prevent double-measurement
 * Returns the runId used. */
export const useGatedMeasureNowIfReady = () =>
  useRecoilCallback(
    ({ snapshot, set }) =>
      (endMarkName: string, ready: boolean, additional?: MeasureDetail): boolean => {
        if (!ready) return false;

        const currentActiveMeasurement = snapshot
          .getLoadable(latestByEndMarkAtom(endMarkName))
          .valueMaybe();
        if (!currentActiveMeasurement) {
          return false;
        }

        const detail: MeasureDetail = {
          ...(currentActiveMeasurement.detail ?? {}),
          ...(additional ?? {}),
        };
        const loadID = detail.loadID;

        // TTL
        const startedAt = detail.startedAt;
        const ttl = ttlThresholds[endMarkName];
        if (ttl != null && typeof startedAt === 'number' && performance.now() - startedAt > ttl) {
          set(latestByEndMarkAtom(endMarkName), null);

          const staleStartMark = createMarkForId(
            currentActiveMeasurement.startMarkName,
            currentActiveMeasurement.id
          );
          performance.clearMarks(staleStartMark);
          return false;
        }

        // Only if the required previous measurement has been measured with the correct loadID, will we measure this
        // If not, abandon the measurement
        const requiredPrev = prevEndMark[endMarkName];
        if (requiredPrev) {
          const prevMeasured = snapshot
            .getLoadable(latestMeasuredByEndMarkAtom(requiredPrev))
            .valueMaybe();
          const prevLoadID = prevMeasured?.detail?.loadID;
          if (!prevMeasured || !loadID || loadID !== prevLoadID) {
            return false;
          }
        }

        // If this end mark conflicts with another end mark (e.g. submit vs abandon), clear the other one
        const conflicting = conflictingEndMark[endMarkName];
        if (conflicting) {
          const conflictedActive = snapshot
            .getLoadable(latestByEndMarkAtom(conflicting))
            .valueMaybe();
          if (conflictedActive) {
            set(latestByEndMarkAtom(conflicting), null);
            const conflictedStartMark = createMarkForId(
              conflictedActive.startMarkName,
              conflictedActive.id
            );
            performance.clearMarks(conflictedStartMark);
          }
        }

        const ok = measureNowIfStarted({
          metricName: currentActiveMeasurement.metricName,
          startMarkName: currentActiveMeasurement.startMarkName,
          endMarkName,
          id: currentActiveMeasurement.id,
          detail,
        });

        if (ok) {
          set(latestByEndMarkAtom(endMarkName), null);
          set(latestMeasuredByEndMarkAtom(endMarkName), {
            ...currentActiveMeasurement,
            detail,
          });
        }

        return ok;
      },
    []
  );

/**
 *
 * @param endMarkName - the end mark to measure
 * @param ready - whether the flow being measured is ready
 *
 * Measure the metric after the next paint, if the start mark exists and the `ready` flag is true.
 * - Gets the latest active entry for the given end mark
 * - Checks to see if the start mark exists for that entry
 * - If so, marks the end and measures after the next paint
 * - Clears the active entry to prevent double-measurement
 */
export function useMeasureAfterFirstPaintIfReady(
  endMarkName: string,
  ready: boolean,
  additionalDetails?: MeasureDetail
) {
  const measuredRef = useRef<string | null>(null);
  const measureGated = useGatedMeasureNowIfReady();

  const measureOnce = useRecoilCallback(
    ({ set, snapshot }) =>
      () => {
        const curr = snapshot.getLoadable(latestByEndMarkAtom(endMarkName)).valueMaybe();
        if (!curr) {
          return;
        }

        if (measuredRef.current === curr.id) {
          return;
        }

        const ok = measureGated(endMarkName, ready, additionalDetails);

        if (ok) {
          measuredRef.current = curr.id;
          set(latestByEndMarkAtom(endMarkName), null);
          set(latestMeasuredByEndMarkAtom(endMarkName), {
            ...curr,
            detail: { ...(curr.detail ?? {}), ...(additionalDetails ?? {}) },
          });
        }
      },
    [additionalDetails, endMarkName, measureGated, ready]
  );

  useEffect(() => {
    if (!ready) {
      return;
    }

    // Cancel the scheduled measurement on unmount so measureOnce can't read a
    // Recoil snapshot after <RecoilRoot> has been torn down. (VX-3970)
    return afterPaint(measureOnce);
  }, [ready, measureOnce]);
}

export const trackWorklistAction = (
  action: AnalyticEventName,
  detail?: Record<string, unknown>
) => {
  analytics.track(action, detail ? { worklist_metadata: detail } : {});
};
